import React, { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import {
    Box,
    Typography,
    Grid,
    useTheme,
    ToggleButton,
    ToggleButtonGroup,
    Button,
    IconButton,
    Tooltip,
    Chip,
    Avatar,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    Divider,
    Link,
} from '@mui/material';
import {
    ChevronLeft,
    ChevronRight,
    Cancel as CancelIcon,
    CheckCircle as CompletedIcon,
    AccessTime,
    Person,
    Topic,
    VideoCall,
    CalendarToday,
    Person as PersonIcon,
    Close as CloseIcon
} from '@mui/icons-material';
import moment from 'moment';
import { LoginType, serviceOptions } from '../../common/const';
import theme from '../../theme';
import { useNavigate } from 'react-router-dom';
import { getStudentData, getUser } from '../../api/authApi';
import LoadingSpinner from '../../common/components/LoadingSpinner';
import CommonSelect from '../../common/components/CommonSelect';
import CommonBookSession from '../../common/components/CommonBookSession';

// Event type colors
const EVENT_TYPE_COLORS = {
    'SAT_ACT_COACHING': '#FF4D4F', // red
    'AP_TUTORING': '#9C27B0', // purple
    'ACADEMIC_TUTORING': theme.palette.secondary.main, // green
    'CODING_SKILLS_TECHNOLOGY': '#3F8CFF', // blue
    'PROJECT_MENTORSHIP': '#E328AF', // pink
    'COUNSELING': '#52C41A', // green
};

// Helper functions - USING YOUR ORIGINAL FORMATDATE
const formatDate = (date) => {
    return date?.toISOString().split('T')[0];
};

const getAllTimeSlots = () => {
    const slots = [];
    for (let hour = 0; hour < 24; hour++) {
        const displayHour = hour % 12 === 0 ? 12 : hour % 12;
        const period = hour >= 12 ? 'PM' : 'AM';
        slots.push({
            display: `${displayHour} ${period}`,
            hour: hour,
            minutes: 0
        });
    }
    return slots;
};

const generateCalendar = (date) => {
    const year = date.getFullYear();
    const month = date.getMonth();
    const firstDay = new Date(year, month, 1);
    const lastDay = new Date(year, month + 1, 0);
    const daysInMonth = lastDay.getDate();

    let startingDay = firstDay.getDay();
    startingDay = startingDay === 0 ? 6 : startingDay - 1; // Monday start

    const days = [];
    const prevMonthDays = new Date(year, month, 0).getDate();

    // Previous month days
    for (let i = 0; i < startingDay; i++) {
        const dayDate = new Date(year, month - 1, prevMonthDays - startingDay + i + 1);
        days.push({
            date: dayDate,
            currentMonth: false,
            day: prevMonthDays - startingDay + i + 1,
            formattedDate: formatDate(dayDate),
            isToday: false,
            isDisabled: true
        });
    }

    // Current month days
    for (let i = 1; i <= daysInMonth; i++) {
        const dayDate = new Date(year, month, i);
        days.push({
            date: dayDate,
            currentMonth: true,
            day: i,
            formattedDate: formatDate(dayDate),
            isToday: dayDate.toDateString() === new Date().toDateString(),
            isDisabled: false
        });
    }

    // Next month days
    const totalCells = 42;
    const remaining = totalCells - days.length;
    for (let i = 1; i <= remaining; i++) {
        const dayDate = new Date(year, month + 1, i);
        days.push({
            date: dayDate,
            currentMonth: false,
            day: i,
            formattedDate: formatDate(dayDate),
            isToday: false,
            isDisabled: true
        });
    }

    return days;
};

// Helper function to get event status
const getEventStatus = (event) => {
    const now = moment();
    const eventEnd = moment(event.session_end_time || event.session_start_time).add(1, 'hour');

    if (event.cancelled_at || event.status === 'CANCELLED') {
        return 'cancelled';
    } else if (eventEnd.isBefore(now)) {
        return 'completed';
    } else {
        return 'upcoming';
    }
};

// Check if join button should be active (10 mins before)
const canJoinMeeting = (event) => {
    const now = moment();
    const eventStart = moment(event.session_start_time);
    const minsUntilStart = eventStart.diff(now, 'minutes');
    return minsUntilStart <= 10 && minsUntilStart > -60;
};

// Get status styles
const getStatusStyles = (status, baseColor) => {
    switch (status) {
        case 'cancelled':
            return {
                backgroundColor: `${baseColor}15`,
                borderLeft: `4px solid ${baseColor}`,
                opacity: 0.6,
                textDecoration: 'line-through',
                color: '#999',
                filter: 'grayscale(0.3)'
            };
        case 'completed':
            return {
                backgroundColor: `${baseColor}10`,
                borderLeft: `4px solid ${baseColor}`,
                opacity: 0.6,
                color: '#666',
                filter: 'grayscale(0.2)'
            };
        default: // upcoming
            return {
                backgroundColor: `${baseColor}25`,
                borderLeft: `4px solid ${baseColor}`,
                opacity: 1,
                color: '#333',
                boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
                fontWeight: 'normal'
            };
    }
};

// Get status icon
const getStatusIcon = (status) => {
    switch (status) {
        case 'cancelled':
            return <CancelIcon sx={{ fontSize: 14, color: '#f44336', mr: 0.5 }} />;
        case 'completed':
            return <CompletedIcon sx={{ fontSize: 14, color: '#4caf50', mr: 0.5 }} />;
        default:
            return null;
    }
};

// Get status chip
const getStatusChip = (status) => {
    switch (status) {
        case 'cancelled':
            return <Chip label="Cancelled" size="small" sx={{ height: 18, fontSize: '10px', bgcolor: '#ffebee', color: '#f44336' }} />;
        case 'completed':
            return <Chip label="Completed" size="small" sx={{ height: 18, fontSize: '10px', bgcolor: '#e8f5e9', color: '#4caf50' }} />;
        default:
            return <Chip label="Upcoming" size="small" sx={{ height: 18, fontSize: '10px', bgcolor: '#e3f2fd', color: '#1976d2' }} />;
    }
};

const DynamicCalendar = ({
    currentDate,
    setCurrentDate,
    selectedDate,
    setSelectedDate,
    viewType,
    setViewType,
    weekDates,
    events = [],
    service,
    setService
}) => {
    const theme = useTheme();
    const [selectedEvent, setSelectedEvent] = useState(null);
    const [popupOpen, setPopupOpen] = useState(false);
    const [popupData, setPopupData] = useState(null); // NEW: Store complete popup data
    const [loadingPopup, setLoadingPopup] = useState(false); // NEW: Loading state
    const navigate = useNavigate();
    const [studentAvatar, setStudentAvatar] = useState(null);
    const [loadingAvatar, setLoadingAvatar] = useState(false);
    const [selectedSession, setSelectedSesion] = useState(null);
    const [open, setOpen] = useState(false);

    // Function to load student avatar
    const loadStudentAvatar = async (studentId) => {
        if (!studentId) return null; // MODIFIED: return null instead of just returning

        setLoadingAvatar(true);
        try {
            const response = await getStudentData(studentId);
            if (response?.data?.user_id) {
                const userResponse = await getUser(response?.data?.user_id);
                if (userResponse?.data?.avatar) {
                    if (userResponse?.data.login_type === LoginType.EMAIL) {
                        const responseData = await fetch(`${process.env.REACT_APP_BASE_URL}/file?bname=${userResponse?.data.avatar}&download=false`, {
                            headers: {
                                'Authorization': `Bearer ${localStorage.getItem('token')}`
                            }
                        });
                        const blob = await responseData.blob();
                        const imageUrl = URL.createObjectURL(blob);
                        return imageUrl; // MODIFIED: return instead of set state
                    } else {
                        return userResponse?.data.avatar; // MODIFIED: return instead of set state
                    }
                }
            }
            return null;
        } catch (error) {
            console.error('Error loading student avatar:', error);
            return null;
        } finally {
            setLoadingAvatar(false);
        }
    };

    const dayContainerRef = useRef(null);
    const weekContainerRef = useRef(null);

    const timeSlots = useMemo(() => getAllTimeSlots(), []);
    const calendarDays = useMemo(() => generateCalendar(currentDate), [currentDate]);
    const weeks = useMemo(() => {
        const weeksArray = [];
        for (let i = 0; i < calendarDays.length; i += 7) {
            weeksArray.push(calendarDays.slice(i, i + 7));
        }
        return weeksArray;
    }, [calendarDays]);

    // FIXED: Get events for a specific date - USING YOUR ORIGINAL APPROACH
    const getEventsForDate = (date) => {
        const dateStr = formatDate(date);
        return events.filter(event => {
            const eventDate = moment(event.session_start_time).format('YYYY-MM-DD');
            return eventDate === dateStr;
        }).sort((a, b) =>
            moment(a.session_start_time).valueOf() - moment(b.session_start_time).valueOf()
        );
    };

    // Generate week dates
    const getWeekDates = (date) => {
        const startOfWeek = moment(date).startOf('isoWeek');
        const weekDates = [];

        for (let i = 0; i < 7; i++) {
            const day = moment(startOfWeek).add(i, 'days');
            weekDates.push({
                date: day.toDate(),
                day: day.date(),
                month: day.month(),
                year: day.year(),
                dayName: day.format('ddd'),
                formattedDate: day.format('YYYY-MM-DD'),
                isToday: day.isSame(new Date(), 'day'),
                isDisabled: false
            });
        }

        return weekDates;
    };

    const displayWeekDates = weekDates || getWeekDates(currentDate);

    // Navigation handlers
    const handlePrev = () => {
        const newDate = new Date(currentDate);
        if (viewType === 'day') {
            newDate.setDate(newDate.getDate() - 1);
            setCurrentDate(newDate);
            setSelectedDate(newDate);
        } else if (viewType === 'week') {
            newDate.setDate(newDate.getDate() - 7);
            setCurrentDate(newDate);
        } else {
            newDate.setMonth(newDate.getMonth() - 1);
            setCurrentDate(newDate);
        }
    };

    const handleNext = () => {
        const newDate = new Date(currentDate);
        if (viewType === 'day') {
            newDate.setDate(newDate.getDate() + 1);
            setCurrentDate(newDate);
            setSelectedDate(newDate);
        } else if (viewType === 'week') {
            newDate.setDate(newDate.getDate() + 7);
            setCurrentDate(newDate);
        } else {
            newDate.setMonth(newDate.getMonth() + 1);
            setCurrentDate(newDate);
        }
    };

    const handleToday = () => {
        const today = new Date();
        setCurrentDate(today);
        setSelectedDate(today);
    };

    const handleViewChange = (event, newView) => {
        if (newView !== null) {
            setViewType(newView);
        }
    };

    // MODIFIED: Load data first, then open popup
    const handleEventClick = async (event) => {
        setLoadingPopup(true);

        // Load avatar
        const avatar = await loadStudentAvatar(event?.student_id);

        // Set all data at once
        setPopupData({
            event,
            avatar
        });

        // Open popup only after all data is ready
        setPopupOpen(true);
        setLoadingPopup(false);
    };

    // Cleanup object URLs when component unmounts
    useEffect(() => {
        return () => {
            if (studentAvatar && studentAvatar.startsWith('blob:')) {
                URL.revokeObjectURL(studentAvatar);
            }
            // Also cleanup popup data avatar
            if (popupData?.avatar && popupData.avatar.startsWith('blob:')) {
                URL.revokeObjectURL(popupData.avatar);
            }
        };
    }, [studentAvatar, popupData]);

    // MODIFIED: Clear popup data after closing
    const handleClosePopup = () => {
        setPopupOpen(false);
        // Clear data after animation completes
        setTimeout(() => {
            setPopupData(null);
            setSelectedEvent(null);
        }, 200);
    };

    const getHeaderText = () => {
        const monthNames = ['January', 'February', 'March', 'April', 'May', 'June',
            'July', 'August', 'September', 'October', 'November', 'December'];

        if (viewType === 'day') {
            return selectedDate.toLocaleDateString('en-US', {
                month: 'long',
                day: 'numeric',
                year: 'numeric'
            });
        } else if (viewType === 'week') {
            const start = displayWeekDates[0];
            const end = displayWeekDates[6];
            return `${monthNames[start.month]} ${start.day} - ${monthNames[end.month]} ${end.day}, ${start.year}`;
        } else {
            return `${monthNames[currentDate.getMonth()]}, ${currentDate.getFullYear()}`;
        }
    };

    // Tooltip content
    const getTooltipContent = (event) => {
        const status = getEventStatus(event);
        return (
            <Box sx={{ p: 1, minWidth: 200 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                    <Typography variant="subtitle2" fontWeight="bold">
                        {event?.student?.user?.first_name} {event?.student?.user?.last_name}
                    </Typography>
                    {getStatusChip(status)}
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.5 }}>
                    <Topic sx={{ fontSize: 14, color: '#666' }} />
                    <Typography variant="body2">
                        {event.topic || serviceOptions.find(a => a.value === event.service_name)?.label}
                    </Typography>
                </Box>

                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                    <AccessTime sx={{ fontSize: 14, color: '#666' }} />
                    <Typography variant="body2">
                        {moment(event.session_start_time).format('hh:mm A')} -
                        {moment(event.session_start_time).add(1, 'hour').format('hh:mm A')}
                    </Typography>
                </Box>
            </Box>
        );
    };

    // Event Popup Component - MODIFIED to use popupData
    const EventPopup = ({ open, data, onClose }) => {
        if (!data || !data.event) return null;

        const { event, avatar } = data;
        const status = getEventStatus(event);
        const canJoin = canJoinMeeting(event);
        const studentName = `${event?.student?.user?.first_name || ''} ${event?.student?.user?.last_name || ''}`;
        const studentInitial = event?.student?.user?.first_name?.[0] || '?';

        return (
            <Fragment>
                <Dialog
                    open={open}
                    onClose={onClose}
                    maxWidth="sm"
                    fullWidth
                    PaperProps={{
                        sx: {
                            borderRadius: '12px',
                            p: 1
                        }
                    }}
                >
                    <DialogTitle sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center',
                        pb: 1
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="h6" fontWeight="bold">Session Details</Typography>
                            {getStatusChip(status)}
                        </Box>
                        <IconButton onClick={onClose} size="small">
                            <CloseIcon />
                        </IconButton>
                    </DialogTitle>

                    <DialogContent dividers>
                        {/* Student Info */}
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, mb: 3 }}>
                            <Avatar
                                src={avatar} // MODIFIED: Use avatar from data
                                sx={{
                                    width: 56,
                                    height: 56,
                                    bgcolor: EVENT_TYPE_COLORS[event.service_name] || '#999',
                                    fontSize: '24px'
                                }}
                            >
                                {!avatar && studentInitial}
                            </Avatar>
                            <Box>
                                <Typography variant="h6" fontWeight="bold">
                                    {studentName}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                    Student
                                </Typography>
                                {/* REMOVED loadingAvatar text since we load before opening */}
                            </Box>
                        </Box>

                        <Divider sx={{ my: 2 }} />

                        {/* Session Details */}
                        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
                            {/* Topic */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Topic sx={{ color: '#666', mt: 0.3 }} />
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Topic</Typography>
                                    <Typography variant="body1" fontWeight="500">
                                        {event.topic || serviceOptions.find(a => a.value === event.service_name)?.label}
                                    </Typography>
                                    {event.description && (
                                        <Typography variant="body2" color="text.secondary" sx={{ mt: 0.5 }}>
                                            {event.description}
                                        </Typography>
                                    )}
                                </Box>
                            </Box>

                            {/* Date & Time */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <AccessTime sx={{ color: '#666', mt: 0.3 }} />
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Date & Time</Typography>
                                    <Typography variant="body1" fontWeight="500">
                                        {moment(event.session_start_time).format('dddd, MMMM D, YYYY')}
                                    </Typography>
                                    <Typography variant="body2">
                                        {moment(event.session_start_time).format('hh:mm A')} -
                                        {moment(event.session_end_time).format('hh:mm A')}
                                    </Typography>
                                </Box>
                            </Box>

                            {/* Meeting Link */}
                            {event.meeting_link && (
                                <Box sx={{ display: 'flex', gap: 2 }}>
                                    <VideoCall sx={{ color: '#666', mt: 0.3 }} />
                                    <Box>
                                        <Typography variant="body2" color="text.secondary">Meeting Link</Typography>
                                        <Link
                                            href={event.meeting_link}
                                            target="_blank"
                                            rel="noopener"
                                            sx={{
                                                display: 'inline-block',
                                                maxWidth: '300px',
                                                overflow: 'hidden',
                                                textOverflow: 'ellipsis',
                                                whiteSpace: 'nowrap'
                                            }}
                                        >
                                            {event.meeting_link}
                                        </Link>
                                    </Box>
                                </Box>
                            )}

                            {/* Service Type */}
                            <Box sx={{ display: 'flex', gap: 2 }}>
                                <Box sx={{
                                    width: 20,
                                    height: 20,
                                    borderRadius: '4px',
                                    bgcolor: EVENT_TYPE_COLORS[event.service_name] || '#999',
                                    mt: 0.3
                                }} />
                                <Box>
                                    <Typography variant="body2" color="text.secondary">Service Type</Typography>
                                    <Typography variant="body1" fontWeight="500">
                                        {event.service_name?.replace(/_/g, ' ')}
                                    </Typography>
                                </Box>
                            </Box>
                        </Box>
                    </DialogContent>

                    <DialogActions sx={{ p: 2, gap: 1, flexWrap: 'wrap' }}>
                        {status !== 'cancelled' && (
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<VideoCall />}
                                onClick={() => {
                                    window.open(event.meeting_link)
                                    onClose();
                                }}
                                disabled={!canJoin || !event.meeting_link}
                                sx={{ flex: 1 }}
                            >
                                Join Meeting
                            </Button>
                        )}

                        {!(status === 'completed' || status === 'cancelled') && (
                            <Button
                                variant="outlined"
                                startIcon={<CalendarToday />}
                                onClick={() => {
                                    setSelectedSesion(event?.reschedule_url);
                                    setOpen(true);
                                    onClose();
                                }}
                                sx={{ flex: 1 }}
                            >
                                Reschedule
                            </Button>
                        )}

                        {!(status === 'completed' || status === 'cancelled') && (
                            <Button
                                variant="outlined"
                                color="error"
                                startIcon={<CancelIcon />}
                                onClick={() => {
                                    setSelectedSesion(event?.cancel_url);
                                    setOpen(true);
                                    onClose();
                                }}
                                sx={{ flex: 1 }}
                            >
                                Cancel
                            </Button>
                        )}

                        <Button
                            variant="text"
                            startIcon={<PersonIcon />}
                            onClick={() => {
                                navigate(`/student-detail/${event.student_id}&${event.service_name}&${event?.mentor_session_id}`)
                                onClose();
                            }}
                            sx={{ flex: 1 }}
                        >
                            View Profile
                        </Button>
                    </DialogActions>
                </Dialog>
            </Fragment>
        );
    };

    // Render Day View - UPDATED with Google Calendar style
    const renderDayView = () => {
        const dayEvents = getEventsForDate(selectedDate);
        const totalHeight = timeSlots.length * 60;

        return (
            <Box
                ref={dayContainerRef}
                sx={{
                    height: '600px',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px',
                    overflow: 'auto',
                    display: 'flex',
                    position: 'relative',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: '#555',
                    }
                }}
            >
                {/* Fixed Time Column */}
                <Box sx={{
                    width: 80,
                    bgcolor: '#fafafa',
                    flexShrink: 0,
                    position: 'sticky',
                    left: 0,
                    zIndex: 3
                }}>
                    {timeSlots.map((slot, index) => (
                        <Box
                            key={index}
                            sx={{
                                height: 60,
                                display: 'flex',
                                alignItems: 'center',
                                justifyContent: 'flex-end',
                                pr: 2,
                                borderBottom: `2px dashed ${theme.palette.divider}`
                            }}
                        >
                            <Typography fontWeight={600} variant="body2" color={theme.palette.background.dark}>
                                {slot.display}
                            </Typography>
                        </Box>
                    ))}
                </Box>

                {/* Schedule Area */}
                <Box sx={{
                    flex: 1,
                    position: 'relative',
                    minWidth: '400px',
                    minHeight: `${totalHeight}px`,
                    borderLeft: `2px dashed ${theme.palette.divider}`,
                }}>
                    {/* Time grid lines */}
                    {timeSlots.map((slot, index) => (
                        <Box
                            key={index}
                            sx={{
                                height: 60,
                                borderBottom: `2px dashed ${theme.palette.divider}`,
                                position: 'absolute',
                                top: `${index * 60}px`,
                                left: 0,
                                right: 0
                            }}
                        />
                    ))}

                    {/* Events - Google Calendar Style */}
                    {dayEvents.map(event => {
                        const eventTime = moment(event.session_start_time);
                        const hour = eventTime.hours();
                        const minute = eventTime.minutes();
                        const startPosition = hour * 60 + minute;

                        const status = getEventStatus(event);
                        const baseColor = EVENT_TYPE_COLORS[event.service_name] || '#999';
                        const statusStyles = getStatusStyles(status, baseColor);

                        return (
                            <Tooltip
                                key={event.event_id}
                                title={getTooltipContent(event)}
                                arrow
                                placement="top"
                            >
                                <Box
                                    onClick={() => handleEventClick(event)}
                                    sx={{
                                        position: 'absolute',
                                        left: 8,
                                        right: 8,
                                        top: `${startPosition}px`,
                                        height: '50px',
                                        p: '6px 8px',
                                        borderRadius: '4px',
                                        zIndex: 2,
                                        cursor: 'pointer',
                                        overflow: 'hidden',
                                        transition: 'all 0.2s ease',
                                        ...statusStyles,
                                        '&:hover': {
                                            opacity: 0.9,
                                            transform: 'scale(1.01)',
                                            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                                            zIndex: 10
                                        }
                                    }}
                                >
                                    {/* Header with name and status icon */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 0.5 }}>
                                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                            <Person sx={{ fontSize: 12, color: 'inherit' }} />
                                            <Typography variant="caption" fontWeight="bold" noWrap fontSize="11px">
                                                {`${event?.student?.user?.first_name} ${event?.student?.user?.last_name}`}
                                            </Typography>
                                        </Box>
                                        {getStatusIcon(status)}
                                    </Box>

                                    {/* Topic */}
                                    <Typography variant="caption" noWrap fontSize="10px" sx={{ display: 'block', mb: 0.5 }}>
                                        {event.topic || serviceOptions.find((a) => a.value === event.service_name)?.label}
                                    </Typography>

                                    {/* Time */}
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                        <AccessTime sx={{ fontSize: 10, color: 'inherit' }} />
                                        <Typography variant="caption" sx={{ fontSize: '9px', opacity: 0.9 }}>
                                            {moment(event.session_start_time).format('hh:mm A')}
                                        </Typography>
                                    </Box>
                                </Box>
                            </Tooltip>
                        );
                    })}
                </Box>
            </Box>
        );
    };

    // Render Week View - UPDATED with Google Calendar style
    const renderWeekView = () => {
        const totalHeight = timeSlots.length * 60;
        return (
            <Box
                ref={weekContainerRef}
                sx={{
                    height: '600px',
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: '8px',
                    overflow: 'auto',
                    '&::-webkit-scrollbar': {
                        width: '6px',
                    },
                    '&::-webkit-scrollbar-track': {
                        background: '#f1f1f1',
                    },
                    '&::-webkit-scrollbar-thumb': {
                        background: '#888',
                        borderRadius: '3px',
                    },
                    '&::-webkit-scrollbar-thumb:hover': {
                        background: '#555',
                    }
                }}
            >
                {/* Day Boxes */}
                <Grid container sx={{
                    bgcolor: '#fafafa',
                    p: 1,
                    pl: 10,
                    position: 'sticky',
                    top: 0,
                    zIndex: 3
                }}>
                    {displayWeekDates.map(date => {
                        const isToday = date.isToday;
                        return (
                            <Grid item xs key={date.formattedDate}>
                                <Box
                                    sx={{
                                        height: '80px',
                                        margin: '4px',
                                        p: 1,
                                        backgroundColor: isToday ? theme.palette.primary.light : 'white',
                                        position: 'relative',
                                        display: 'flex',
                                        flexDirection: 'column',
                                        justifyContent: 'center',
                                        alignItems: 'center',
                                        cursor: 'pointer',
                                        borderRadius: '12px',
                                        border: `1px solid ${theme.palette.divider}`,
                                    }}
                                >
                                    <Typography variant="body2" fontWeight="bold" color="text.secondary" fontSize="18px">
                                        {date.dayName}
                                    </Typography>
                                    <Typography
                                        sx={{
                                            fontWeight: isToday ? 'bold' : 600,
                                            color: isToday ? theme.palette.primary.dark : theme.palette.text.primary,
                                            fontSize: 18,
                                            mt: 0.5
                                        }}
                                    >
                                        {date.day}
                                    </Typography>
                                </Box>
                            </Grid>
                        );
                    })}
                </Grid>

                {/* Time Grid Container */}
                <Box sx={{
                    display: 'flex',
                    minHeight: `${totalHeight}px`,
                    minWidth: '800px'
                }}>
                    {/* Fixed Time Column */}
                    <Box sx={{
                        width: 80,
                        bgcolor: '#fafafa',
                        flexShrink: 0,
                        position: 'sticky',
                        left: 0,
                        zIndex: 2
                    }}>
                        {timeSlots.map((slot, index) => (
                            <Box
                                key={index}
                                sx={{
                                    height: 60,
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'flex-end',
                                    pr: 2,
                                }}
                            >
                                <Typography fontWeight={600} variant="body2" color={theme.palette.background.dark}>
                                    {slot.display}
                                </Typography>
                            </Box>
                        ))}
                    </Box>
                    <Box sx={{
                        display: 'flex',
                        flex: 1,
                    }}>
                        {displayWeekDates.map((date, dayIndex) => {
                            const dateEvents = getEventsForDate(date.date);
                            return (
                                <Box
                                    key={date.formattedDate}
                                    sx={{
                                        flex: 1,
                                        borderLeft: dayIndex > 0 ? `2px dashed ${theme.palette.divider}` : 'none',
                                        borderRight: dayIndex === 6 ? `2px dashed ${theme.palette.divider}` : 'none',
                                        position: 'relative',
                                        bgcolor: date.isToday ? '#f8fbff' : 'transparent'
                                    }}
                                >
                                    {/* Time slots grid */}
                                    {timeSlots.map((slot, index) => (
                                        <Box
                                            key={index}
                                            sx={{
                                                height: 60,
                                                borderBottom: `2px dashed ${theme.palette.divider}`,
                                                position: 'absolute',
                                                top: `${index * 60}px`,
                                                left: 0,
                                                right: 0
                                            }}
                                        />
                                    ))}

                                    {/* Events - Google Calendar Style */}
                                    {dateEvents.map(event => {
                                        const eventTime = moment(event.session_start_time);
                                        const hour = eventTime.hours();
                                        const minute = eventTime.minutes();
                                        const startPosition = hour * 60 + minute;

                                        const status = getEventStatus(event);
                                        const baseColor = EVENT_TYPE_COLORS[event.service_name] || '#999';
                                        const statusStyles = getStatusStyles(status, baseColor);

                                        return (
                                            <Tooltip
                                                key={event.event_id}
                                                title={getTooltipContent(event)}
                                                arrow
                                                placement="top"
                                            >
                                                <Box
                                                    onClick={() => handleEventClick(event)}
                                                    sx={{
                                                        position: 'absolute',
                                                        left: 4,
                                                        right: 4,
                                                        top: `${startPosition}px`,
                                                        height: '40px',
                                                        p: '2px 4px',
                                                        borderRadius: '4px',
                                                        zIndex: 1,
                                                        cursor: 'pointer',
                                                        overflow: 'hidden',
                                                        transition: 'all 0.2s ease',
                                                        ...statusStyles,
                                                        '&:hover': {
                                                            opacity: 0.9,
                                                            transform: 'scale(1.01)',
                                                            boxShadow: '0 4px 8px rgba(0,0,0,0.15)',
                                                            zIndex: 10
                                                        }
                                                    }}
                                                >
                                                    {/* Name with status icon */}
                                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                                        {getStatusIcon(status)}
                                                        <Typography variant="caption" fontWeight="bold" noWrap fontSize="9px">
                                                            {`${event?.student?.user?.first_name}`}
                                                        </Typography>
                                                    </Box>

                                                    {/* Topic (truncated) */}
                                                    <Typography variant="caption" noWrap fontSize="8px" sx={{ display: 'block' }}>
                                                        {event.topic?.substring(0, 15) ||
                                                            serviceOptions.find((a) => a.value === event.service_name)?.label?.substring(0, 15)}
                                                    </Typography>

                                                    {/* Time */}
                                                    <Typography variant="caption" noWrap fontSize="7px" sx={{ display: 'block' }}>
                                                        {moment(event.session_start_time).format('h:mm A')}
                                                    </Typography>
                                                </Box>
                                            </Tooltip>
                                        );
                                    })}
                                </Box>
                            );
                        })}
                    </Box>
                </Box>
            </Box>
        );
    };

    // Render Month View - UPDATED with proper event display and today highlighting
    const renderMonthView = () => {
        return (
            <Box sx={{
                border: `1px solid ${theme.palette.background.secondary}`,
                borderRadius: '8px',
                overflow: 'hidden',
                background: 'white'
            }}>
                {/* Week Days Header */}
                <Box sx={{
                    p: '16px 8px 0px 0px',
                    bgcolor: 'white',
                }}>
                    <Grid container>
                        {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map((day, index) => (
                            <Grid item xs key={index}>
                                <Typography align="center" fontWeight={600} color={theme.palette.background.dark}>
                                    {day}
                                </Typography>
                            </Grid>
                        ))}
                    </Grid>
                </Box>

                <Box sx={{ p: 1 }}>
                    {weeks.map((week, weekIndex) => (
                        <Grid container key={weekIndex} sx={{ mb: weekIndex < 5 ? 0.5 : 0 }}>
                            {week.map((day, dayIndex) => {
                                const isToday = day.isToday;
                                const isCurrentMonth = day.currentMonth;
                                const isDisabled = day.isDisabled;
                                // Get UTC date from calendar day
                                const dayYear = day.date.getFullYear();
                                const dayMonth = day.date.getMonth();
                                const dayDate = day.date.getDate();

                                // Filter events that match this UTC date
                                const eventList = events.filter(event => {
                                    if (!event.session_start_time) return false;

                                    const eventDate = new Date(event.session_start_time);
                                    return eventDate.getUTCFullYear() === dayYear &&
                                        eventDate.getUTCMonth() === dayMonth &&
                                        eventDate.getUTCDate() === dayDate;
                                });

                                return (
                                    <Grid item xs={12 / 7} key={dayIndex}>
                                        <Tooltip
                                            title={
                                                eventList.length > 0 ? (
                                                    <Box>
                                                        {eventList.map((event, idx) => {
                                                            const status = getEventStatus(event);
                                                            return (
                                                                <Box key={idx} sx={{
                                                                    p: 0.5,
                                                                    borderBottom: idx < eventList.length - 1 ? '1px solid #444' : 'none',
                                                                    textDecoration: status === 'cancelled' ? 'line-through' : 'none',
                                                                    opacity: status === 'completed' ? 0.6 : 1,
                                                                }}>
                                                                    <Typography variant="body2" fontWeight="bold">
                                                                        {event?.student?.user?.first_name} {event?.student?.user?.last_name}
                                                                    </Typography>
                                                                    <Typography variant="caption" display="block">
                                                                        {moment(event.session_start_time).format('hh:mm A')} -
                                                                        {event.topic || serviceOptions.find(a => a.value === event.service_name)?.label}
                                                                    </Typography>
                                                                </Box>
                                                            );
                                                        })}
                                                    </Box>
                                                ) : ''
                                            }
                                            arrow
                                            placement="top"
                                        >
                                            <Box
                                                sx={{
                                                    height: '120px',
                                                    margin: '5px',
                                                    p: 1,
                                                    backgroundColor: isToday
                                                        ? 'white'
                                                        : (isCurrentMonth ? 'white' : theme.palette.grey[100]),
                                                    position: 'relative',
                                                    display: 'flex',
                                                    flexDirection: 'column',
                                                    cursor: isDisabled ? 'default' : 'pointer',
                                                    borderRadius: '10px',
                                                    border: isToday
                                                        ? `2px solid ${theme.palette.primary.main}`
                                                        : (eventList.length ? `1.5px solid ${theme.palette.secondary.main}` : `1px solid ${theme.palette.divider}`),
                                                    opacity: isDisabled ? 0.6 : 1,
                                                }}
                                            >
                                                {/* Day Number */}
                                                <Typography
                                                    sx={{
                                                        fontWeight: isToday ? 700 : (isCurrentMonth ? 600 : 400),
                                                        color: isToday
                                                            ? theme.palette.primary.dark
                                                            : (isCurrentMonth ? theme.palette.text.primary : theme.palette.text.disabled),
                                                        fontSize: 20,
                                                        mb: 0.5
                                                    }}
                                                >
                                                    {day.day}
                                                </Typography>

                                                {!isDisabled && eventList.length > 0 && (
                                                    <Box sx={{
                                                        overflow: 'hidden',
                                                        flex: 1,
                                                        display: 'flex',
                                                        flexDirection: 'column',
                                                        gap: 0.3
                                                    }}>
                                                        {eventList.slice(0, 3).map((event, idx) => {
                                                            const status = getEventStatus(event);
                                                            const baseColor = EVENT_TYPE_COLORS[event.service_name] || '#999';

                                                            return (
                                                                <Tooltip
                                                                    key={idx}
                                                                    title={getTooltipContent(event)}
                                                                    arrow
                                                                    placement="right"
                                                                >
                                                                    <Box
                                                                        onClick={(e) => {
                                                                            e.stopPropagation();
                                                                            handleEventClick(event);
                                                                        }}
                                                                        sx={{
                                                                            display: 'flex',
                                                                            alignItems: 'center',
                                                                            gap: 0.5,
                                                                            p: '2px 4px',
                                                                            borderRadius: '3px',
                                                                            backgroundColor: status === 'upcoming' ? `${baseColor}15` : 'transparent',
                                                                            borderLeft: `2px solid ${baseColor}`,
                                                                            opacity: status === 'completed' ? 0.6 : status === 'cancelled' ? 0.5 : 1,
                                                                            textDecoration: status === 'cancelled' ? 'line-through' : 'none',
                                                                            cursor: 'pointer',
                                                                            '&:hover': {
                                                                                backgroundColor: `${baseColor}25`,
                                                                            }
                                                                        }}
                                                                    >
                                                                        {getStatusIcon(status)}
                                                                        <Typography variant="caption" noWrap fontSize="8px" sx={{ minWidth: 35 }}>
                                                                            {moment(event.session_start_time).format('h:mm')}
                                                                        </Typography>
                                                                        <Typography variant="caption" noWrap fontSize="8px" fontWeight="bold">
                                                                            {event?.student?.user?.first_name}
                                                                        </Typography>
                                                                    </Box>
                                                                </Tooltip>
                                                            );
                                                        })}

                                                        {eventList.length > 3 && (
                                                            <Typography variant="caption" sx={{ fontSize: '8px', color: '#666', mt: 0.3 }}>
                                                                +{eventList.length - 3} more
                                                            </Typography>
                                                        )}
                                                    </Box>
                                                )}
                                            </Box>
                                        </Tooltip>
                                    </Grid>
                                );
                            })}
                        </Grid>
                    ))}
                </Box>
            </Box>
        );
    };

    return (
        <Box sx={{ p: '16px 0px' }}>
            <CommonBookSession
                bookingUrl={selectedSession}
                open={open}
                setOpen={setOpen}
                handleClose={(data) => {
                    setOpen(false);
                }}
            />
            <Box sx={{
                display: 'flex',
                justifyContent: 'space-between',
                alignItems: 'center',
                mb: 2
            }}>
                {/* Left: Today button first, then arrows, then header text */}
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, width: '70%' }}>
                    {/* Today Button FIRST */}
                    <Button
                        variant="contained"
                        onClick={handleToday}
                        size="medium"
                        sx={{
                            borderRadius: '6px',
                            textTransform: 'none',
                            fontWeight: 600,
                            bgcolor: 'white',
                            color: '#333',
                            minWidth: '90px',
                            height: '40px',
                            fontSize: '14px',
                            boxShadow: 'none',
                            border: `1px solid ${theme.palette.divider}`,
                            '&:hover': {
                                bgcolor: 'white',
                                boxShadow: 'none'
                            }
                        }}
                    >
                        Today
                    </Button>

                    {/* Left Arrow */}
                    <IconButton
                        onClick={handlePrev}
                        size="medium"
                        sx={{
                            color: 'text.secondary',
                            p: 1
                        }}
                    >
                        <ChevronLeft fontSize="medium" />
                    </IconButton>

                    {/* Right Arrow */}
                    <IconButton
                        onClick={handleNext}
                        size="medium"
                        sx={{
                            color: 'text.secondary',
                            p: 1
                        }}
                    >
                        <ChevronRight fontSize="medium" />
                    </IconButton>

                    {/* Header Text */}
                    <Typography variant="h4" fontWeight="bold" sx={{ fontSize: '24px', ml: 1 }}>
                        {getHeaderText()}
                    </Typography>
                    <CommonSelect
                        name="service"
                        placeholder="Select Service"
                        value={service}
                        onChange={(e) => setService(e.target.value)}
                        options={serviceOptions}
                        allowClear
                        width="35%"
                    />
                </Box>

                {/* Right: View Toggle */}
                <ToggleButtonGroup
                    value={viewType}
                    exclusive
                    onChange={handleViewChange}
                    size="medium"
                    sx={{
                        gap: 1.5,
                        '& .MuiToggleButton-root': {
                            px: 3,
                            py: 1,
                            textTransform: 'none',
                            fontWeight: 600,
                            fontSize: '15px',
                            border: `1px solid ${theme.palette.divider}`,
                            borderRadius: '8px',
                            height: '40px',
                            '&.Mui-selected': {
                                backgroundColor: theme.palette.primary.main,
                                color: theme.palette.secondary.main,
                                borderColor: theme.palette.primary.main,
                                '&:hover': {
                                    backgroundColor: theme.palette.primary.main
                                }
                            },
                            '&:not(.Mui-selected)': {
                                backgroundColor: theme.palette.background.secondary,
                                color: theme.palette.background.dark,
                                '&:hover': {
                                    backgroundColor: theme.palette.background.secondary,
                                },
                                border: 'none'
                            },
                        }
                    }}
                >
                    <ToggleButton value="day">Daily</ToggleButton>
                    <ToggleButton value="week">Weekly</ToggleButton>
                    <ToggleButton value="month">Monthly</ToggleButton>
                </ToggleButtonGroup>
            </Box>

            {/* Main Calendar Area */}
            <Box sx={{ minHeight: '600px', mb: 2 }}>
                {viewType === 'day' && renderDayView()}
                {viewType === 'week' && renderWeekView()}
                {viewType === 'month' && renderMonthView()}
            </Box>

            {/* Status Legend */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 4, flexWrap: 'wrap', mb: 2 }}>
                <Box display="flex" gap={1} alignItems="center">
                    <Box sx={{ width: 16, height: 16, borderRadius: '3px', bgcolor: '#4CAF50', opacity: 1 }} />
                    <Typography variant="body2" fontWeight={500}>Upcoming (Bright)</Typography>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                    <Box sx={{ width: 16, height: 16, borderRadius: '3px', bgcolor: '#9E9E9E', opacity: 0.5 }} />
                    <Typography variant="body2" fontWeight={500}>Completed (Faded)</Typography>
                </Box>
                <Box display="flex" gap={1} alignItems="center">
                    <Box sx={{
                        width: 16,
                        height: 16,
                        borderRadius: '3px',
                        bgcolor: '#f44336',
                        opacity: 0.5,
                        position: 'relative',
                        '&::after': {
                            content: '""',
                            position: 'absolute',
                            top: '50%',
                            left: 0,
                            right: 0,
                            height: '1px',
                            bgcolor: '#f44336',
                            transform: 'translateY(-50%)'
                        }
                    }} />
                    <Typography variant="body2" fontWeight={500}>Cancelled (Strikethrough)</Typography>
                </Box>
            </Box>

            {/* Service Type Legend */}
            <Box sx={{
                display: 'flex',
                justifyContent: 'center',
                gap: 4,
                flexWrap: 'wrap'
            }}>
                {serviceOptions.map(item => (
                    <Box key={item?.value} display="flex" gap={1} alignItems="center">
                        <Box sx={{
                            width: 12,
                            height: 12,
                            borderRadius: '50%',
                            bgcolor: EVENT_TYPE_COLORS[item?.value]
                        }} />
                        <Typography variant="body2" fontWeight={500}>
                            {item?.label}
                        </Typography>
                    </Box>
                ))}
            </Box>

            {/* Loading Backdrop - NEW */}
            {loadingPopup && (
                <LoadingSpinner />
            )}

            {/* Event Popup - MODIFIED to use popupData */}
            <EventPopup
                open={popupOpen}
                data={popupData}
                onClose={handleClosePopup}
            />
        </Box>
    );
};

export default DynamicCalendar;